import { useEffect, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  categoryOf,
  findsCaseInArchive,
  flagDestinations,
  isFinalCheckRefusal,
  nextStage as nextOnRoute,
  photoPath,
  routeForCategory,
  type Kit,
  type NoteThread,
  type Stage,
} from '@/domain';
import {
  useActivityRepository,
  useArchiveRepository,
  useClock,
  useKitRepository,
  useOutbox,
} from '../container';
import { liveRefetchInterval } from '../live';
import { EVERY_PAGE_SIZE, everyPage } from '../paging';
import { QueuedWhileOfflineError, dispatch } from '../outbox/dispatch';
import { keys } from '../query-keys';
import type {
  AssignOptions,
  CompleteWorkResult,
  KitFilter,
  StartWorkResult,
  StopWorkResult,
} from '../ports/KitRepository';
import { useTimerStore } from '../timer/timerStore';
import { useObjectUrls } from './useObjectUrls';
import { useSession } from './useSession';

/**
 * The technician path, section 5.2 and section 7.4.
 *
 * A use case knows the rule and the port. It does not know HTTP exists, and it
 * is where the optimistic behaviour lives so that no screen has to reimplement
 * it. Section 8.3 permits optimism on exactly three interactions: start work,
 * complete work and mark urgent. Everything else waits for the server.
 */

function requestId(): string {
  return crypto.randomUUID();
}

export function useMyQueue(filter: KitFilter) {
  const repo = useKitRepository();
  return useQuery({
    queryKey: keys.kits.list(filter),
    queryFn: () => repo.list(filter),
    staleTime: 15_000,
    refetchInterval: liveRefetchInterval,
    /*
     * The same list asked for another page or in another order keeps the rows in
     * hand until the new ones arrive. Without it a stage's table unmounted while
     * the next page loaded, and mounting again reset it: the reader pressed Next,
     * saw page two for a moment and was sent back to page one, and lost what they
     * had typed in the search. The origin sorts of section 2 of the request of
     * 24 September 2026 are asked of the server too, and met the same.
     *
     * A different list, another bench, starts from nothing, rather than showing
     * one bench's trays under another bench's name.
     */
    placeholderData: (previous, previousQuery) =>
      sameList(previousQuery?.queryKey[2] as KitFilter | undefined, filter) ? previous : undefined,
  });
}

/**
 * A bench's whole queue, every page of it, for the queue screen, which shows it
 * all at once and sorts it itself. Under the kits list prefix, so whatever
 * refreshes or optimistically updates the lists reaches it too. Section 25 of
 * the review of 2 October 2026: it asked for one page of two hundred.
 */
export function useWholeQueue(stage: Stage | undefined) {
  const repo = useKitRepository();
  return useQuery({
    queryKey: keys.kits.list({ stage, every: true }),
    queryFn: () => everyPage((page) => repo.list({ stage, page, pageSize: EVERY_PAGE_SIZE })),
    staleTime: 15_000,
    refetchInterval: liveRefetchInterval,
  });
}

/**
 * Every case given to this person by name, at whichever bench it is. A case may
 * be assigned to anybody (request of 5 October 2026), so a technician's queue
 * is their bench AND what was handed to them elsewhere; the bench list alone
 * left a case assigned from another department out of Mine.
 */
export function useAssignedToMe(userId: string | undefined) {
  const repo = useKitRepository();
  return useQuery({
    queryKey: keys.kits.list({ assignedToUserId: userId, every: true }),
    queryFn: () =>
      everyPage((page) => repo.list({ assignedToUserId: userId, page, pageSize: EVERY_PAGE_SIZE })),
    enabled: userId !== undefined && userId !== '',
    staleTime: 15_000,
    refetchInterval: liveRefetchInterval,
  });
}

/** Whether two filters ask for the same trays, whichever page and order they ask for them in. */
function sameList(before: KitFilter | undefined, after: KitFilter): boolean {
  if (before === undefined) return false;
  const which = (filter: KitFilter) => [
    filter.stage,
    filter.assignedToUserId,
    filter.mine,
    filter.urgent,
    filter.rework,
    filter.jobOrderNo,
    filter.q,
  ];
  return JSON.stringify(which(before)) === JSON.stringify(which(after));
}

export function useKit(kitId: string | undefined) {
  const repo = useKitRepository();
  return useQuery({
    queryKey: keys.kits.byId(kitId ?? ''),
    queryFn: () => repo.byId(kitId as string),
    enabled: kitId !== undefined,
    refetchInterval: liveRefetchInterval,
  });
}

export function useKitHistory(kitId: string | undefined) {
  const repo = useKitRepository();
  return useQuery({
    queryKey: keys.kits.history(kitId ?? ''),
    queryFn: () => repo.history(kitId as string),
    enabled: kitId !== undefined,
  });
}

/**
 * Everything that happened to a case, from its creation to its delivery, newest
 * first. Refetched on the live interval as the kit is: the rows are written by
 * whoever is working the case, on whatever screen they are on.
 */
export function useCaseActivity(jobOrderNo: string | undefined) {
  const repo = useActivityRepository();
  return useQuery({
    queryKey: keys.caseActivity(jobOrderNo ?? ''),
    queryFn: () => repo.forCase(jobOrderNo as string),
    enabled: jobOrderNo !== undefined,
    refetchInterval: liveRefetchInterval,
  });
}

/** How often an open Lab Notes conversation asks for new messages. */
export const NOTES_OPEN_REFETCH_MS = 4_000;

/**
 * A case's Lab Notes and how far this person has read them. Request of
 * 4 October 2026, section 7.
 *
 * Always from the network: `staleTime` is nought, so every open asks the
 * server, and the stream refreshes it the moment somebody writes. While the
 * conversation is on screen (`open`) it is also asked every four seconds, so a
 * phone whose stream has dropped still sees a reply within a few seconds.
 */
export function useNoteThread(kitId: string | undefined, open = false) {
  const repo = useKitRepository();
  return useQuery({
    queryKey: keys.kits.notes(kitId ?? ''),
    queryFn: () => repo.noteThread(kitId as string),
    enabled: kitId !== undefined,
    staleTime: 0,
    refetchInterval: open ? NOTES_OPEN_REFETCH_MS : liveRefetchInterval,
  });
}

/** The notes alone, newest first, for the screens that list them. */
export function useKitNotes(kitId: string | undefined) {
  const thread = useNoteThread(kitId);
  return { ...thread, data: thread.data?.items };
}

/**
 * Sending a Lab Note. The screen shows it at once as sending and keeps it on
 * screen as failed with a retry if the server does not take it; the request id
 * is the screen's, so a retry of the same message is written once.
 */
export function useSendLabNote() {
  const repo = useKitRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      kitId: string;
      text: string;
      requestId: string;
      visibleToUserId: string | null;
    }) => repo.addNote(input.kitId, input.text, input.requestId, input.visibleToUserId),
    onSuccess: (note, input) => {
      queryClient.setQueryData<NoteThread>(keys.kits.notes(input.kitId), (thread) =>
        thread === undefined || thread.items.some((item) => item.id === note.id)
          ? thread
          : { ...thread, items: [note, ...thread.items] },
      );
    },
    onSettled: (_result, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.notes(input.kitId) });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/** Records that this person has read a case's Lab Notes, which clears the count on the button. */
export function useMarkNotesRead() {
  const repo = useKitRepository();
  const clock = useClock();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (kitId: string) => repo.markNotesRead(kitId),
    onSuccess: (_result, kitId) => {
      const now = clock.now().toISOString();
      queryClient.setQueryData<NoteThread>(keys.kits.notes(kitId), (thread) =>
        thread === undefined ? thread : { ...thread, lastReadAt: now },
      );
    },
  });
}

/**
 * Mark a kit urgent, or take it back.
 *
 * Invalidates the order lists as well as the kit, because urgency changes the
 * sort on the orders screen and the board, and a manager who marks a case
 * urgent expects to see it move to the top without a reload.
 */
export function useMarkUrgent() {
  const repo = useKitRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { kitId: string; urgent: boolean; reason?: string }) =>
      repo.markUrgent(input.kitId, input.urgent, input.reason),
    onSettled: (_result, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.byId(input.kitId) });
      void queryClient.invalidateQueries({ queryKey: keys.kits.all });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      void queryClient.invalidateQueries({ queryKey: keys.orders.all });
      void queryClient.invalidateQueries({ queryKey: keys.board() });
      // The urgent flag is printed on the sheet.
      void queryClient.invalidateQueries({ queryKey: keys.printAll });
      // A doctor's and a patient's page list the case with its urgent dot, and
      // since the request of 6 October 2026 (fifth) the mark is made there, in
      // the case opened over the list.
      void queryClient.invalidateQueries({ queryKey: keys.doctors.all });
      void queryClient.invalidateQueries({ queryKey: keys.patients.all });
    },
  });
}

/** Thrown by `useFindCase` for a code that names nothing. The screen words it. */
export class UnknownCaseError extends Error {
  constructor() {
    super('unknown case');
    this.name = 'UnknownCaseError';
  }
}

/**
 * Turns whatever somebody typed into the find box into the case it names.
 *
 * Three shapes are accepted, because three things are printed on a tray card
 * and a job sheet: a kit id, a QR value and a job order number. A job order
 * resolves to its first kit, which under the one-tray rule is its only kit.
 * Anything else is refused rather than guessed at.
 *
 * A job order is looked up among the trays on the floor, except for somebody
 * whose door to a case is the record in the archive: Accounts as the lab starts
 * with them, who hold the archive and not Orders, and who may not list the
 * floor's trays while Payments, Items and Archive are all they hold (the
 * request of 5 October 2026 (third), section 10, as the request of
 * 6 October 2026 (fourth) left it). For them the record itself is asked for,
 * which also finds a case that has been shipped or archived and is on no
 * bench. Such a case may have no tray, so `kitId` can be null; the job order
 * number is always there.
 */
export function useFindCase() {
  const repo = useKitRepository();
  const archive = useArchiveRepository();
  const session = useSession();

  return useMutation({
    mutationFn: async (raw: string): Promise<{ kitId: string | null; jobOrderNo: string }> => {
      const code = raw.trim().toUpperCase();

      if (/^K-\d+$/.test(code)) {
        const kit = await repo.byId(code);
        return { kitId: kit.id, jobOrderNo: kit.jobOrderNo };
      }
      if (/^QR-\d+$/.test(code)) {
        const kit = await repo.byQr(code);
        return { kitId: kit.id, jobOrderNo: kit.jobOrderNo };
      }
      if (/^JO-\d+$/.test(code)) {
        if (findsCaseInArchive(session.data?.views ?? [])) {
          const record = await archive.record(code);
          return { kitId: record.kits[0]?.id ?? null, jobOrderNo: record.jobOrder.jobOrderNo };
        }
        const page = await repo.list({ jobOrderNo: code, pageSize: 1 });
        const kit = page.items[0];
        if (kit === undefined) throw new UnknownCaseError();
        return { kitId: kit.id, jobOrderNo: kit.jobOrderNo };
      }
      throw new UnknownCaseError();
    },
  });
}

/**
 * The photographs on a case.
 *
 * Everyone who can open the case sees them, which is the point: reception
 * photographs the impression and the patient's mouth at intake so the bench
 * has something to compare its work against, and the bench adds its own on the
 * way through.
 */
export function useKitPhotos(kitId: string | undefined) {
  const repo = useKitRepository();
  return useQuery({
    queryKey: keys.kits.photos(kitId ?? ''),
    queryFn: () => repo.photos(kitId as string),
    enabled: kitId !== undefined,
  });
}

/**
 * The people a kit may be given to: whoever works its bench, by the server's
 * rule. Filtering the team list here would be a second copy of that rule, and
 * the two would drift. Section 1.3 of the request of 13 September 2026.
 */
export function useKitAssignees(kitId: string | undefined, stage?: Stage) {
  const repo = useKitRepository();
  return useQuery({
    queryKey: keys.kits.assignees(kitId ?? '', stage),
    /*
     * `stage` asks who works a DIFFERENT bench from the one the case is on, which
     * is what Mark done needs: the case is going somewhere, and the people who
     * take it are the people there. Section 5.3 of the request of 20 September
     * 2026. Omitted means the kit's own bench, which is what assigning means.
     */
    queryFn: () => repo.assignees(kitId as string, stage),
    enabled: kitId !== undefined,
    staleTime: 30_000,
  });
}

/**
 * Give a case to one person, or clear it. Not optimistic: the server refuses
 * somebody off the bench, and showing a name it is about to refuse is worse
 * than a moment's wait. Invalidates the kit and every list, the board, which
 * shows who is working, the orders, which carry the holder's name, and the log.
 */
export function useAssignKit() {
  const repo = useKitRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { kitId: string; userId: string | null } & AssignOptions) =>
      repo.assign(input.kitId, input.userId, {
        note: input.note,
        noteVisibility: input.noteVisibility,
        forwardNoteIds: input.forwardNoteIds,
      }),
    onSuccess: (kit) => {
      queryClient.setQueryData(keys.kits.byId(kit.id), kit);
    },
    onSettled: (_result, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.byId(input.kitId) });
      void queryClient.invalidateQueries({ queryKey: keys.kits.all });
      void queryClient.invalidateQueries({ queryKey: keys.board() });
      void queryClient.invalidateQueries({ queryKey: keys.orders.all });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      void queryClient.invalidateQueries({ queryKey: keys.kits.notes(input.kitId) });
      // The Technician column of a doctor's and a patient's cases, where a case
      // is now assigned from, opened over the list. The request of
      // 6 October 2026 (fifth).
      void queryClient.invalidateQueries({ queryKey: keys.doctors.all });
      void queryClient.invalidateQueries({ queryKey: keys.patients.all });
    },
  });
}

/**
 * Recording the ingot press or the zircon block from the job view.
 *
 * NOT through the outbox. The four work commands are queued because a bench in
 * a dead spot must be able to finish a tray; correcting a material is a desk
 * job that can wait for a signal, and a refusal a technician must READ is worth
 * more than a write that silently succeeds later against a rule the server may
 * refuse.
 */
export function useSetKitSpecification() {
  const repo = useKitRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { kitId: string; patch: { ingot?: string; block?: string } }) =>
      repo.setSpecification(input.kitId, input.patch),
    onSuccess: (kit) => {
      queryClient.setQueryData(keys.kits.byId(kit.id), kit);
    },
    onSettled: (_result, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.byId(input.kitId) });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/**
 * One photograph, as something an `<img>` can actually display.
 *
 * The list from `useKitPhotos` carries URLs, and those URLs cannot be put on an
 * `<img>`: the endpoint is authenticated and the browser sends no token on an
 * image request, so every photograph in the application rendered as a broken
 * one. The bytes are fetched through the port instead and handed over as an
 * object URL.
 *
 * Returns null while it loads and if it fails. A thumbnail that will not load
 * is a gap in a strip, not a screen worth failing: the case still opens and the
 * bench still works.
 */
export function useCasePhotoUrl(url: string | undefined): string | null {
  const repo = useKitRepository();

  const { data } = useQuery({
    queryKey: keys.kits.photoBlob(url ?? ''),
    queryFn: () => repo.photoBlob(url as string),
    enabled: url !== undefined && url !== '',
    // A photograph is written once and never rewritten, and its id is in its
    // own URL. Refetching one costs a megabyte of somebody's mobile data.
    staleTime: Infinity,
  });

  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (data === undefined) {
      setObjectUrl(null);
      return;
    }

    const created = URL.createObjectURL(data);
    setObjectUrl(created);

    // Revoked when the photograph leaves the screen. Without this a technician
    // who opens forty cases in a shift holds every image they have seen until
    // the tab is closed, which on a shared floor phone is the whole shift.
    return () => URL.revokeObjectURL(created);
  }, [data]);

  return objectUrl;
}

/** One photograph of several: something an `<img>` can display, and whether its bytes failed. */
export interface CasePhotoBytes {
  /** An object URL, or null while the bytes are on their way, when they failed, or when none were asked for. */
  url: string | null;
  /** The bytes could not be fetched: the photograph is not coming, as opposed to not here yet. */
  failed: boolean;
}

/**
 * Several photographs at once, in the order asked for; `undefined` is a place
 * in the list that asks for nothing. For the image viewer. The request of
 * 5 October 2026 (third), section 6.
 *
 * The same cache entries as `useCasePhotoUrl`, so a photograph whose tile is
 * on the screen is here at once, with no second fetch. The object URLs are
 * this caller's own, though: a tile revokes its URL when it leaves the screen,
 * and the viewer stays open over a list that is refreshing underneath it. Two
 * addresses of one file (a thumbnail that is the photograph itself, as on
 * every one stored before thumbnails) answer the same URL.
 */
export function useCasePhotoUrls(urls: readonly (string | undefined)[]): CasePhotoBytes[] {
  const repo = useKitRepository();

  /*
   * One query a file, however many places in the list ask for it: the
   * thumbnail and the full file of an older photograph are the same file, and
   * two observers of one key in one list is a thing the library warns about.
   */
  const files = new Map<string, string>();
  for (const url of urls) {
    if (url === undefined || url === '') continue;
    const path = photoPath(url);
    if (!files.has(path)) files.set(path, url);
  }
  const paths = [...files.keys()];

  const results = useQueries({
    queries: paths.map((path) => ({
      queryKey: keys.kits.photoBlob(path),
      queryFn: () => repo.photoBlob(files.get(path) as string),
      staleTime: Infinity,
    })),
  });

  const objectUrls = useObjectUrls(results.map((result) => result.data));

  return urls.map((url) => {
    const at = url === undefined || url === '' ? -1 : paths.indexOf(photoPath(url));
    return { url: objectUrls[at] ?? null, failed: results[at]?.isError === true };
  });
}

/**
 * Saves one photograph as a file: the bytes of the full file, for the caller
 * to hand to the device. The request of 5 October 2026 (third), section 6.
 *
 * A mutation although it is a GET, because the server writes a row in the log
 * for every download it answers: it runs when somebody taps Download and at no
 * other time, is never cached, and is never tried again by itself. A refusal
 * arrives as the server worded it.
 */
export function useDownloadPhoto() {
  const repo = useKitRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (photoId: string) => repo.downloadPhoto(photoId),
    retry: false,
    // The download is in the case's log now.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['activity'] }),
  });
}

/**
 * Start work, from a scan or by hand.
 *
 * The timer starts optimistically because the delay is felt on a factory floor
 * and the rollback is cheap. A refusal -- a 403 from a bench that is not yours --
 * rolls it straight back. There is no longer a 409 for a second task: section 2
 * of the request of 20 September 2026 allows as many as the person has trays.
 *
 * The optimistic clock starts from THIS device's clock, and the server's answer
 * replaces it a moment later with its own `startedAt` and whatever the open entry
 * had already accumulated. Section 1: the server's timestamps are the truth.
 */
export function useStartWork() {
  const repo = useKitRepository();
  const clock = useClock();
  const outbox = useOutbox();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { kitId: string; source: 'qr' | 'manual'; takeOver?: boolean }) => {
      const createdAt = clock.now().toISOString();
      // Made here and not inside the command: it is also the name of the outbox
      // entry, which the refusal below has to find again.
      const id = requestId();
      try {
        return await dispatch(
          outbox,
          {
            kind: 'start',
            kitId: input.kitId,
            createdAt,
            command: {
              kitId: input.kitId,
              startedAt: createdAt,
              source: input.source,
              // Sent only when it is being asked for, so an ordinary start carries
              // nothing new. Section 7.4 of the request of 20 September 2026.
              ...(input.takeOver === true ? { takeOver: true } : {}),
              requestId: id,
            },
          },
          (command) => repo.start(command),
        );
      } catch (error) {
        /*
         * A case at Final Check that this person may not take. The request of
         * 5 October 2026 (third), section 1, asks for that to be a message with
         * one OK button and not an error, so its outbox entry does not stay
         * behind as a refused command: the person has just read why and pressed
         * OK, and a "Not synced" mark after that would say something went wrong
         * when nothing did. No work is lost, because none was done.
         *
         * This one refusal only, and only when it arrives while the person is
         * looking. Every other refusal stays in the tray until somebody discards
         * it, and so does this one when it comes back from a start replayed
         * after a dead spot, where nobody saw a message.
         */
        if (isFinalCheckRefusal(error)) {
          // The refusal is what the screen has to hear, whatever the tray does.
          await outbox.discard(id).catch(() => undefined);
        }
        throw error;
      }
    },

    onMutate: (input) => {
      const previous = useTimerStore.getState().running;
      useTimerStore
        .getState()
        .start(input.kitId, { startedAt: clock.now().getTime(), accumulatedSeconds: 0 });
      return { previous };
    },

    onError: (error, _input, context) => {
      /*
       * The screen offered a take the server refused, so what it knows of the
       * lab's setting is out of date: read it again, and the button goes. The
       * case itself is refetched below, as after every start.
       */
      if (isFinalCheckRefusal(error)) {
        void queryClient.invalidateQueries({ queryKey: keys.session });
      }
      if (!context) return;
      // A queued command will still start the task, so the timer stays running.
      // Only a refusal means the technician is not working this kit.
      if (error instanceof QueuedWhileOfflineError) return;
      useTimerStore.setState({ running: context.previous });
    },

    onSuccess: (result: StartWorkResult) => {
      queryClient.setQueryData(keys.kits.byId(result.kit.id), result.kit);
      /*
       * The server's own two facts replace the optimistic guess: when the run
       * began by its clock, and what the open entry already held from an earlier
       * run of the same stage. A task taken up after lunch then reads the sum
       * rather than starting again from zero. Section 1.3.
       */
      useTimerStore.getState().start(result.kit.id, {
        startedAt: Date.parse(result.activeTask.startedAt),
        accumulatedSeconds: result.historyEntry.activeSeconds,
      });
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.all });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      void queryClient.invalidateQueries({ queryKey: keys.board() });
    },
  });
}

/**
 * Pause. The command carries nothing: the server computes the run's length from
 * its own timestamps, section 1.4.
 *
 * Only THIS task stops. Section 2: the person's other cases keep running, and a
 * pause that stopped them all is what the single-task store used to do.
 */
export function usePauseWork() {
  const repo = useKitRepository();
  const queryClient = useQueryClient();

  return useMutation({
    // Not queued. A pause that arrives late is worse than one that is lost:
    // replaying it after the technician started again would stop a running task.
    mutationFn: (input: { kitId: string }) => repo.pause({ ...input, requestId: requestId() }),
    onMutate: (input) => {
      // Stops at the time it had reached, rather than reading zero.
      useTimerStore.getState().pause(input.kitId);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.all });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      void queryClient.invalidateQueries({ queryKey: keys.board() });
    },
  });
}

/**
 * A manager stops somebody else's clock on a tray, from the board. Request of
 * 25 September 2026.
 *
 * Not queued, for the reason Pause is not: a stop that arrives after the
 * technician has scanned the tray again would stop the work they came back to.
 * Nothing optimistic either. The clock being stopped is not on this device, so
 * there is nothing here to stop; the board refetches and the chip goes.
 */
export function useStopWork() {
  const repo = useKitRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { kitId: string }): Promise<StopWorkResult> =>
      repo.stop({ ...input, requestId: requestId() }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.board() });
      void queryClient.invalidateQueries({ queryKey: keys.kits.all });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/**
 * The tasks this person has running, from the server.
 *
 * Section 1.5: a reopened browser must show the running clock at once rather than
 * zero, and since the device keeps no count there is nowhere else for it to come
 * from. Seeded into the timer store, which every clock reads.
 */
export function useRunningTasks() {
  const repo = useKitRepository();
  const seed = useTimerStore((state) => state.seed);

  const query = useQuery({
    queryKey: keys.kits.running(),
    queryFn: () => repo.running(),
    // The clocks are computed from `startedAt`, so a stale list is only wrong
    // about WHICH tasks are running, which changes when this device acts.
    staleTime: 30_000,
  });

  useEffect(() => {
    if (query.data === undefined) return;
    seed(
      Object.fromEntries(
        query.data.map((task) => [
          task.kitId,
          { startedAt: Date.parse(task.startedAt), accumulatedSeconds: task.accumulatedSeconds },
        ]),
      ),
    );
  }, [query.data, seed]);

  return query;
}

/**
 * Mark done.
 *
 * A normal kit advances along its own route and the destination is not asked
 * for. Only a flagged or rework kit stops and asks, which is item 4 of section
 * 17: the exception is manual, the rule is not.
 */
export function useCompleteWork() {
  const repo = useKitRepository();
  const clock = useClock();
  const outbox = useOutbox();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      kit: Kit;
      nextStage: Stage | null;
      assignToUserId?: string | null;
      /** For Lab Notes, optional. Request of 4 October 2026, section 5. */
      note?: string;
    }) => {
      const createdAt = clock.now().toISOString();
      return dispatch(
        outbox,
        {
          kind: 'complete',
          kitId: input.kit.id,
          createdAt,
          command: {
            kitId: input.kit.id,
            completedAt: createdAt,
            nextStage: input.nextStage,
            assignToUserId: input.assignToUserId ?? null,
            ...(input.note === undefined || input.note.trim() === '' ? {} : { note: input.note.trim() }),
            requestId: requestId(),
          },
        },
        (command) => repo.complete(command),
      );
    },

    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: keys.kits.all });
      const snapshot = queryClient.getQueriesData({ queryKey: keys.kits.all });

      // Optimistically move the kit out of my queue. The destination is only a
      // guess for display; the server's answer replaces it a moment later.
      const guess =
        input.nextStage ?? nextOnRoute(input.kit.stage, input.kit.input, input.kit.product) ?? input.kit.stage;

      queryClient.setQueryData(keys.kits.byId(input.kit.id), {
        ...input.kit,
        stage: guess,
      });

      // This case's clock only. Section 2: whatever else this person has running
      // keeps running, and clearing the store would stop it on screen alone.
      useTimerStore.getState().stop(input.kit.id);
      return { snapshot };
    },

    onError: (error, _input, context) => {
      // A command sitting in the outbox WILL be applied, so rolling the kit
      // back would put it in front of the technician again and invite a second
      // tap. The optimistic move stands and the tray carries the truth.
      if (error instanceof QueuedWhileOfflineError) return;

      // A refusal is different: roll the queue back and let the screen surface
      // the ProblemDetails detail, which for a flagged kit is the prompt to
      // pick a department.
      for (const [key, data] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, data);
      }
    },

    onSuccess: (result: CompleteWorkResult) => {
      queryClient.setQueryData(keys.kits.byId(result.kit.id), result.kit);
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.all });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      void queryClient.invalidateQueries({ queryKey: keys.board() });
      void queryClient.invalidateQueries({ queryKey: keys.orders.all });
    },
  });
}

/**
 * Flag and send to another department. Not optimistic: the destination is a
 * decision, and showing a case as moved before the server agreed is how two
 * benches end up believing they both have it.
 */
export function useFlagKit() {
  const repo = useKitRepository();
  const clock = useClock();
  const outbox = useOutbox();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      kitId: string;
      toStage: Stage;
      note: string;
      assignToUserId?: string | null;
    }) =>
      dispatch(
        outbox,
        {
          kind: 'flag',
          kitId: input.kitId,
          createdAt: clock.now().toISOString(),
          command: { ...input, requestId: requestId() },
        },
        (command) => repo.flag(command),
      ),

    onSuccess: (_result, input) => {
      // This case's clock only. Section 2: the person's other trays are still in
      // the furnace, and clearing the whole store would stop them on screen while
      // the server kept counting.
      useTimerStore.getState().stop(input.kitId);
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.all });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      void queryClient.invalidateQueries({ queryKey: keys.board() });
    },
  });
}

/**
 * Final Check. Section 5.2 and section 7.10 item 5.
 *
 * Only a holder of `finalcheck` may pass or fail, and only on a kit that is in
 * Final Check. The server enforces both; these hooks surface its refusal rather
 * than deciding for it.
 */
export function useQcPass() {
  const repo = useKitRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (kitId: string) => repo.qcPass(kitId),
    onSuccess: (kit) => queryClient.setQueryData(keys.kits.byId(kit.id), kit),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.all });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      void queryClient.invalidateQueries({ queryKey: keys.orders.all });
      void queryClient.invalidateQueries({ queryKey: keys.board() });
    },
  });
}

/**
 * Takes an action back. Section 6 of the request of 5 October 2026.
 *
 * Everything the action touched is read again afterwards, as after the action
 * itself: the case, every list, the board, the orders and the log. The cases
 * put back are written to the cache at once, so the screen the person is on
 * shows the case where it was without waiting for the lists.
 */
export function useUndoWork() {
  const repo = useKitRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (undoId: string) => repo.undo(undoId),
    onSuccess: (result) => {
      for (const kit of result.items) queryClient.setQueryData(keys.kits.byId(kit.id), kit);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.all });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      void queryClient.invalidateQueries({ queryKey: keys.orders.all });
      void queryClient.invalidateQueries({ queryKey: keys.board() });
    },
  });
}

export function useQcRework() {
  const repo = useKitRepository();
  const clock = useClock();
  const outbox = useOutbox();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      kitId: string;
      toStage: Stage;
      note: string;
      assignToUserId?: string | null;
    }) =>
      dispatch(
        outbox,
        {
          kind: 'qcRework',
          kitId: input.kitId,
          createdAt: clock.now().toISOString(),
          command: { ...input, requestId: requestId() },
        },
        (command) => repo.qcRework(command),
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.all });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
      void queryClient.invalidateQueries({ queryKey: keys.orders.all });
      void queryClient.invalidateQueries({ queryKey: keys.board() });
    },
  });
}

export function useAddKitNote() {
  const repo = useKitRepository();
  const queryClient = useQueryClient();

  return useMutation({
    // One key per note, so a retry of the same tap writes it once.
    mutationFn: (input: { kitId: string; text: string }) =>
      repo.addNote(input.kitId, input.text, crypto.randomUUID()),
    onSettled: (_result, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.byId(input.kitId) });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/**
 * The proofs of finished work on a case, arranged apart from the case
 * photographs: the caller's own, or everybody's for a holder of seeWorkProof.
 * The server decides which; the hook shows what it is given.
 */
export function useKitWorkProofs(kitId: string | undefined) {
  const repo = useKitRepository();
  return useQuery({
    queryKey: keys.kits.workProofs(kitId ?? ''),
    queryFn: () => repo.workProofs(kitId as string),
    enabled: kitId !== undefined,
  });
}

/**
 * A photograph of the finished work, taken at Mark done before the completion.
 *
 * Through the outbox, in front of the completion that follows it: in a dead
 * spot the photograph queues, the completion queues behind it on the same kit,
 * and the replay sends them in that order. A refusal is not queued; the dialog
 * shows it and the case does not move on.
 */
export function useAddWorkProof() {
  const repo = useKitRepository();
  const clock = useClock();
  const outbox = useOutbox();
  const queryClient = useQueryClient();

  return useMutation({
    /*
     * The sheet gives each photograph its own request id and keeps it, so a
     * retry after a failure part way through a set writes each photograph once.
     * The note rides with the photograph. Request of 4 October 2026, section 6b.
     */
    mutationFn: (input: { kitId: string; file: File; requestId?: string; note?: string }) => {
      const id = input.requestId ?? requestId();
      const note = input.note?.trim() ?? '';
      return dispatch(
        outbox,
        {
          kind: 'workProof',
          kitId: input.kitId,
          createdAt: clock.now().toISOString(),
          command: { kitId: input.kitId, file: input.file, requestId: id, ...(note === '' ? {} : { note }) },
        },
        (command) => repo.addWorkProof(command.kitId, command.file, command.requestId, command.note),
      );
    },
    onSettled: (_result, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.workProofs(input.kitId) });
      void queryClient.invalidateQueries({ queryKey: keys.kits.history(input.kitId) });
      void queryClient.invalidateQueries({ queryKey: keys.kits.notes(input.kitId) });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/**
 * Takes a case photograph off a case.
 *
 * Every list that counts photographs is refetched, because the count is on the
 * stage tables and the drawer as well as on the case itself, and a count that
 * disagrees with the strip beside it is worse than either.
 */
export function useRemoveKitPhoto() {
  const repo = useKitRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kitId, photoId }: { kitId: string; photoId: string }) =>
      repo.removePhoto(kitId, photoId),
    onSettled: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useAddKitPhoto() {
  const repo = useKitRepository();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { kitId: string; file: File }) =>
      repo.addPhoto(input.kitId, input.file, crypto.randomUUID()),
    onSettled: (_result, _error, input) => {
      void queryClient.invalidateQueries({ queryKey: keys.kits.byId(input.kitId) });
      void queryClient.invalidateQueries({ queryKey: keys.kits.photos(input.kitId) });
      void queryClient.invalidateQueries({ queryKey: ['activity'] });
    },
  });
}

/** The stages a flagged case may be sent to, minus the one it is on. */
export function flagDestinationsFor(kit: Kit): Stage[] {
  return flagDestinations(kit.stage);
}

/**
 * True when the kit is off its own route, so completing it needs a
 * destination. Pass the item's category when it is known: a renamed item's new
 * name is on no list the domain was built with, and its category still is.
 */
export function needsExplicitDestination(
  kit: Kit,
  category: string = categoryOf(kit.product),
): boolean {
  return kit.rework || !routeForCategory(kit.input, category).includes(kit.stage);
}
